import "./styles.css";
import type { RealtimeChannel } from "@supabase/supabase-js";
import { GameApi } from "./lib/game-api";
import {
  dailyShareText,
  formatCompact,
  formatCountdown,
  formatDangerScore,
  waveIndex,
} from "./lib/game-state";
import { requestCaptchaToken } from "./lib/turnstile";
import type {
  ActiveRoom,
  DailySnapshot,
  GameMode,
  MultiplayerLeaderboard,
  QueuePlayer,
  RoomSnapshot,
} from "./types";

function byId<T extends HTMLElement>(id: string): T {
  const value = document.getElementById(id);
  if (!value) throw new Error(`Missing #${id}`);
  return value as T;
}

const elements = {
  notice: byId<HTMLDivElement>("notice"),
  noticeText: byId<HTMLSpanElement>("notice-text"),
  captcha: byId<HTMLDivElement>("captcha"),
  multiplayerTab: byId<HTMLButtonElement>("multiplayer-tab"),
  roomsTab: byId<HTMLButtonElement>("rooms-tab"),
  dailyTab: byId<HTMLButtonElement>("daily-tab"),
  multiplayerMode: byId<HTMLElement>("multiplayer-mode"),
  roomsMode: byId<HTMLElement>("rooms-mode"),
  dailyMode: byId<HTMLElement>("daily-mode"),
  headerStreak: byId<HTMLElement>("header-streak"),
  headerDanger: byId<HTMLElement>("header-danger"),
  roomNumber: byId<HTMLElement>("room-number"),
  turnTitle: byId<HTMLElement>("turn-title"),
  roomActionButton: byId<HTMLButtonElement>("room-action-button"),
  turnClock: byId<HTMLElement>("turn-clock"),
  turnCountdown: byId<HTMLElement>("turn-countdown"),
  multiplayerBoardWrap: byId<HTMLElement>("multiplayer-board-wrap"),
  multiplayerGrid: byId<HTMLDivElement>("multiplayer-grid"),
  resetBanner: byId<HTMLElement>("reset-banner"),
  resetFinder: byId<HTMLElement>("reset-finder"),
  currentStreak: byId<HTMLElement>("current-streak"),
  currentDanger: byId<HTMLElement>("current-danger"),
  dangerFill: byId<HTMLElement>("danger-fill"),
  bestStreak: byId<HTMLElement>("best-streak"),
  findsStat: byId<HTMLElement>("finds-stat"),
  personalClicks: byId<HTMLElement>("personal-clicks"),
  seatCount: byId<HTMLElement>("seat-count"),
  playerQueue: byId<HTMLOListElement>("player-queue"),
  globalClicks: byId<HTMLElement>("global-clicks"),
  globalFinds: byId<HTMLElement>("global-finds"),
  myRank: byId<HTMLElement>("my-rank"),
  leaderboardBody: byId<HTMLElement>("leaderboard-body"),
  activeRoomCount: byId<HTMLElement>("active-room-count"),
  activeGamesList: byId<HTMLElement>("active-games-list"),
  refreshRooms: byId<HTMLButtonElement>("refresh-rooms"),
  dailyDate: byId<HTMLElement>("daily-date"),
  dailyKicker: byId<HTMLElement>("daily-kicker"),
  dailyStatusTitle: byId<HTMLElement>("daily-status-title"),
  dailyScore: byId<HTMLElement>("daily-score"),
  dailyGrid: byId<HTMLDivElement>("daily-grid"),
  dailyQueuedCount: byId<HTMLElement>("daily-queued-count"),
  dailyResetCountdown: byId<HTMLElement>("daily-reset-countdown"),
  dailyResult: byId<HTMLElement>("daily-result"),
  dailyFinalScore: byId<HTMLElement>("daily-final-score"),
  dailyResultKicker: byId<HTMLElement>("daily-result-kicker"),
  dailyResultTitle: byId<HTMLElement>("daily-result-title"),
  dailyResultCopy: byId<HTMLElement>("daily-result-copy"),
  shareDaily: byId<HTMLButtonElement>("share-daily"),
  dailySurvival: byId<HTMLElement>("daily-survival"),
  dailyBest: byId<HTMLElement>("daily-best"),
  dailyCompleted: byId<HTMLElement>("daily-completed"),
  dailyPerfects: byId<HTMLElement>("daily-perfects"),
  deleteData: byId<HTMLButtonElement>("delete-data"),
};

const playerColors = [
  "#ff6b5e", "#4da7ff", "#42d67c", "#ffd84d", "#9c7cff",
  "#ff91c8", "#ff9d45", "#55d7d2", "#b7db55", "#c4a184",
];

const multiplayerButtons: HTMLButtonElement[] = [];
const dailyButtons: HTMLButtonElement[] = [];
let api: GameApi | null = null;
let mode: GameMode = "multiplayer";
let room: RoomSnapshot | null = null;
let leaderboard: MultiplayerLeaderboard | null = null;
let activeRooms: ActiveRoom[] = [];
let daily: DailySnapshot | null = null;
let roomChannel: RealtimeChannel | null = null;
let subscribedRoomId: string | null = null;
let heartbeatTimer = 0;
let tickTimer = 0;
let refreshTimer = 0;
let roomServerOffset = 0;
let dailyServerOffset = 0;
let multiplayerSubmitting = false;
let roomActionInFlight = false;
let roomsLoading = false;
let roomRefreshInFlight = false;
let expirationInFlight = false;
let lastResetState = false;
let dailyQueued: number[] = [];
let dailyInFlight: number[] = [];
let dailySubmitting = false;
let dailyMidnightRefresh = false;
const roomStorageKey = "ptwb-button-room-id";

function storedRoomId(): string | null {
  try {
    return window.localStorage.getItem(roomStorageKey);
  } catch {
    return null;
  }
}

function storeRoomId(roomId: string | null): void {
  try {
    if (roomId) window.localStorage.setItem(roomStorageKey, roomId);
    else window.localStorage.removeItem(roomStorageKey);
  } catch {
    // A blocked localStorage should not prevent the anonymous Supabase session from playing.
  }
}

function focusGridNeighbor(buttons: HTMLButtonElement[], index: number, event: KeyboardEvent): void {
  let next = index;
  let step = 0;
  let minimum = 0;
  let maximum = 99;
  switch (event.key) {
    case "ArrowLeft":
      minimum = Math.floor(index / 10) * 10;
      next = index - 1;
      step = -1;
      break;
    case "ArrowRight":
      maximum = Math.floor(index / 10) * 10 + 9;
      next = index + 1;
      step = 1;
      break;
    case "ArrowUp":
      next = index - 10;
      step = -10;
      break;
    case "ArrowDown":
      next = index + 10;
      step = 10;
      break;
    case "Home":
      minimum = event.ctrlKey ? 0 : Math.floor(index / 10) * 10;
      maximum = event.ctrlKey ? 99 : minimum + 9;
      next = minimum;
      step = 1;
      break;
    case "End":
      minimum = event.ctrlKey ? 0 : Math.floor(index / 10) * 10;
      maximum = event.ctrlKey ? 99 : minimum + 9;
      next = maximum;
      step = -1;
      break;
    default:
      return;
  }
  event.preventDefault();
  while (next >= minimum && next <= maximum && buttons[next]?.disabled) next += step;
  buttons[next]?.focus();
}

function createGrid(container: HTMLElement, prefix: string, target: HTMLButtonElement[], click: (index: number) => void): void {
  const fragment = document.createDocumentFragment();
  for (let index = 0; index < 100; index += 1) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "grid-button";
    button.id = `${prefix}-${index}`;
    button.dataset.index = String(index);
    button.style.setProperty("--wave-index", String(waveIndex(index)));
    button.setAttribute("role", "gridcell");
    button.setAttribute("aria-label", `Button ${index + 1}`);
    button.addEventListener("click", () => click(index));
    button.addEventListener("keydown", (event) => focusGridNeighbor(target, index, event));
    target.push(button);
    fragment.append(button);
  }
  container.replaceChildren(fragment);
}

function setNotice(message?: string, isError = false): void {
  elements.notice.hidden = !message;
  if (!message) return;
  elements.noticeText.textContent = message;
  elements.notice.classList.toggle("is-error", isError);
  elements.notice.querySelector(".spinner")?.toggleAttribute("hidden", isError);
}

function errorText(error: unknown): string {
  if (!error || typeof error !== "object") return String(error || "Unknown error");
  const value = error as { message?: string; details?: string };
  return value.message ?? value.details ?? "Unknown error";
}

function isError(error: unknown, code: string): boolean {
  return errorText(error).includes(code);
}

function updateRoomOffset(snapshot: RoomSnapshot): void {
  roomServerOffset = Date.parse(snapshot.serverNow) - Date.now();
}

function updateDailyOffset(snapshot: DailySnapshot): void {
  dailyServerOffset = Date.parse(snapshot.serverNow) - Date.now();
}

function roomNow(): number {
  return Date.now() + roomServerOffset;
}

function dailyNow(): number {
  return Date.now() + dailyServerOffset;
}

function roomIsResetting(snapshot: RoomSnapshot): boolean {
  return roomNow() < Date.parse(snapshot.round.opensAt);
}

function ownQueuePlayer(snapshot: RoomSnapshot): QueuePlayer | undefined {
  return snapshot.queue.find((player) => player.isYou);
}

function playerForTurn(snapshot: RoomSnapshot): QueuePlayer | undefined {
  return snapshot.queue.find((player) => player.membershipId === snapshot.turn.currentMembershipId);
}

function renderSelectionMarkers(button: HTMLButtonElement, players: QueuePlayer[]): void {
  const markers = document.createElement("span");
  markers.className = "selection-markers";
  for (const player of players) {
    const marker = document.createElement("span");
    marker.className = "selection-marker";
    marker.style.setProperty("--marker-color", playerColors[player.colorIndex % playerColors.length] ?? playerColors[0]!);
    marker.title = `${player.alias}'s advance pick`;
    markers.append(marker);
  }
  button.replaceChildren(markers);
}

function renderMultiplayerGrid(snapshot: RoomSnapshot): void {
  const resetting = roomIsResetting(snapshot);
  const pressed = new Set(
    resetting && snapshot.lastFind ? snapshot.lastFind.pressedIndices : snapshot.round.pressedIndices,
  );
  const active = snapshot.membershipStatus === "active";
  const now = roomNow();
  const turnOpen = snapshot.turn.startedAt !== null && now >= Date.parse(snapshot.turn.startedAt);
  const turnAlive = snapshot.turn.expiresAt !== null && now < Date.parse(snapshot.turn.expiresAt);
  const canPress = navigator.onLine && active && snapshot.turn.isYours && turnOpen && turnAlive && !resetting;
  const canPreselect = navigator.onLine && active && !snapshot.turn.isYours && !resetting;
  const own = ownQueuePlayer(snapshot);
  const selections = new Map<number, QueuePlayer[]>();

  for (const player of snapshot.queue) {
    if (player.preselectedIndex === null) continue;
    const existing = selections.get(player.preselectedIndex) ?? [];
    existing.push(player);
    selections.set(player.preselectedIndex, existing);
  }

  for (let index = 0; index < multiplayerButtons.length; index += 1) {
    const button = multiplayerButtons[index]!;
    const isPressed = pressed.has(index);
    const isOwnSelection = own?.preselectedIndex === index;
    const isHiddenDuringReset = resetting && snapshot.lastFind?.buttonIndex === index;
    button.className = "grid-button";
    button.classList.toggle("is-pressed", isPressed);
    button.classList.toggle("is-preselected", isOwnSelection);
    button.classList.toggle("is-hidden", isHiddenDuringReset);
    if (isOwnSelection && own) {
      button.style.setProperty("--selection-color", playerColors[own.colorIndex % playerColors.length] ?? playerColors[0]!);
    } else {
      button.style.removeProperty("--selection-color");
    }
    button.disabled = isPressed || resetting || multiplayerSubmitting || (!canPress && !canPreselect);
    button.setAttribute("aria-pressed", String(isPressed));
    const markerPlayers = selections.get(index) ?? [];
    renderSelectionMarkers(button, markerPlayers);
    const details = isPressed ? ", already pressed" : isOwnSelection ? ", your advance pick" : markerPlayers.length ? `, selected by ${markerPlayers.length} player${markerPlayers.length === 1 ? "" : "s"}` : "";
    button.setAttribute("aria-label", `Button ${index + 1}${details}`);
  }

  elements.multiplayerBoardWrap.classList.toggle("is-resetting", resetting);
  elements.resetBanner.hidden = !resetting;
  if (resetting && snapshot.lastFind) {
    elements.resetFinder.textContent = `${snapshot.lastFind.finderAlias} found it after ${snapshot.lastFind.clickCount} clicks.`;
  }
}

function renderQueue(snapshot: RoomSnapshot): void {
  const fragment = document.createDocumentFragment();
  for (const [index, player] of snapshot.queue.entries()) {
    const item = document.createElement("li");
    item.className = `queue-player player-color-${player.colorIndex % 10}`;
    item.classList.toggle("is-current", player.isCurrent);
    item.classList.toggle("is-you", player.isYou);

    const color = document.createElement("span");
    color.className = "player-color";
    color.textContent = player.isCurrent ? "▶" : String(index + 1);
    color.setAttribute("aria-hidden", "true");

    const identity = document.createElement("span");
    identity.className = "player-name";
    const name = document.createElement("strong");
    name.textContent = `${player.alias}${player.isYou ? " (you)" : ""}`;
    const score = document.createElement("span");
    score.textContent = `${player.wrongStreak} wrong · ${formatDangerScore(player.dangerScore)} danger`;
    identity.append(name, score);

    const pick = document.createElement("span");
    pick.className = `player-pick${player.preselectedIndex === null ? " is-empty" : ""}`;
    pick.textContent = player.preselectedIndex === null ? "—" : String(player.preselectedIndex + 1);
    pick.title = player.preselectedIndex === null ? "No advance pick" : `Advance pick: button ${player.preselectedIndex + 1}`;
    item.append(color, identity, pick);
    fragment.append(item);
  }
  elements.playerQueue.replaceChildren(fragment);
}

function renderLeaderboard(): void {
  if (!leaderboard) return;
  elements.myRank.textContent = leaderboard.myRank === null
    ? "Build a streak to enter the board."
    : `Your all-time rank: #${leaderboard.myRank}`;
  if (leaderboard.leaders.length === 0) {
    const empty = document.createElement("div");
    empty.className = "empty-leaderboard";
    empty.textContent = "The leaderboard is waiting for its first beautifully wrong streak.";
    elements.leaderboardBody.replaceChildren(empty);
    return;
  }
  const fragment = document.createDocumentFragment();
  for (const entry of leaderboard.leaders) {
    const row = document.createElement("div");
    row.className = `leaderboard-row${entry.isYou ? " is-you" : ""}`;
    row.setAttribute("role", "row");
    const rank = document.createElement("strong");
    rank.textContent = `#${entry.rank}`;
    const alias = document.createElement("span");
    alias.textContent = `${entry.alias}${entry.isYou ? " (you)" : ""}`;
    const streak = document.createElement("strong");
    streak.textContent = String(entry.bestWrongStreak);
    const finds = document.createElement("strong");
    finds.textContent = String(entry.finds);
    const danger = document.createElement("span");
    danger.textContent = String(entry.dangerScore);
    for (const cell of [rank, alias, streak, finds, danger]) cell.setAttribute("role", "cell");
    row.append(rank, alias, streak, finds, danger);
    fragment.append(row);
  }
  elements.leaderboardBody.replaceChildren(fragment);
}

function roomActivityLabel(value: string): string {
  const elapsedSeconds = Math.max(0, Math.floor((Date.now() - Date.parse(value)) / 1000));
  if (elapsedSeconds < 60) return "Active now";
  const minutes = Math.floor(elapsedSeconds / 60);
  if (minutes < 60) return `Active ${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return `Active ${hours}h ago`;
}

function renderActiveRooms(): void {
  elements.activeRoomCount.textContent = `${activeRooms.length} / 100 rooms`;
  if (activeRooms.length === 0) {
    const empty = document.createElement("div");
    empty.className = "empty-rooms";
    empty.textContent = "No active games yet. Open Multiplayer to start the first room.";
    elements.activeGamesList.replaceChildren(empty);
    return;
  }

  const fragment = document.createDocumentFragment();
  for (const activeRoom of activeRooms) {
    const isCurrent = room?.roomId === activeRoom.roomId;
    const seatedElsewhere = room?.membershipStatus === "active" && !isCurrent;
    const card = document.createElement("article");
    card.className = `active-game-card${isCurrent ? " is-current" : ""}`;

    const heading = document.createElement("div");
    const label = document.createElement("p");
    label.className = "eyebrow";
    label.textContent = roomActivityLabel(activeRoom.lastActivityAt);
    const title = document.createElement("h2");
    title.textContent = `Room ${activeRoom.roomNumber}`;
    heading.append(label, title);

    const stats = document.createElement("dl");
    stats.className = "active-room-stats";
    const roomStats: Array<[string, string]> = [
      ["Players", `${activeRoom.playerCount}/10`],
      ["Watching", String(activeRoom.spectatorCount)],
      ["Pressed", `${activeRoom.pressedCount}/100`],
    ];
    for (const [term, value] of roomStats) {
      const stat = document.createElement("div");
      const dt = document.createElement("dt");
      const dd = document.createElement("dd");
      dt.textContent = term;
      dd.textContent = value;
      stat.append(dt, dd);
      stats.append(stat);
    }

    const watch = document.createElement("button");
    watch.type = "button";
    watch.className = isCurrent ? "secondary-button" : "primary-button red-action-button";
    watch.textContent = isCurrent ? "Watching" : "Watch game";
    watch.disabled = isCurrent || seatedElsewhere || roomsLoading || !navigator.onLine;
    if (seatedElsewhere) watch.title = "Leave your current game before switching rooms.";
    watch.addEventListener("click", () => void watchActiveRoom(activeRoom.roomId));
    card.append(heading, stats, watch);
    fragment.append(card);
  }
  elements.activeGamesList.replaceChildren(fragment);
}

function renderRoom(snapshot: RoomSnapshot): void {
  room = snapshot;
  storeRoomId(snapshot.roomId);
  updateRoomOffset(snapshot);
  elements.headerStreak.textContent = String(snapshot.stats.currentWrongStreak);
  elements.headerDanger.textContent = formatDangerScore(snapshot.stats.currentDangerScore);
  elements.roomNumber.textContent = String(snapshot.roomNumber);
  elements.currentStreak.textContent = String(snapshot.stats.currentWrongStreak);
  elements.currentDanger.textContent = formatDangerScore(snapshot.stats.currentDangerScore);
  elements.dangerFill.style.width = `${Math.min(100, (snapshot.stats.currentDangerScore / 99) * 100)}%`;
  elements.bestStreak.textContent = String(snapshot.stats.bestWrongStreak);
  elements.findsStat.textContent = String(snapshot.stats.finds);
  elements.personalClicks.textContent = formatCompact(snapshot.stats.totalClicks);
  elements.globalClicks.textContent = formatCompact(snapshot.global.totalClicks);
  elements.globalFinds.textContent = formatCompact(snapshot.global.totalFinds);
  elements.seatCount.textContent = `${snapshot.queue.length}/10`;

  const current = playerForTurn(snapshot);
  const resetting = roomIsResetting(snapshot);
  const seated = snapshot.membershipStatus === "active";
  const roomFull = snapshot.queue.length >= 10;
  if (snapshot.membershipStatus === "removed") {
    elements.turnTitle.textContent = "Timeouted";
  } else if (!seated) {
    elements.turnTitle.textContent = snapshot.queue.length === 0
      ? "Join game to start"
      : "Join game when ready";
  } else if (resetting) {
    elements.turnTitle.textContent = "A fresh wrong board is coming.";
  } else if (snapshot.turn.isYours) {
    elements.turnTitle.textContent = "Pick a wrong button.";
  } else if (current) {
    elements.turnTitle.textContent = "Pick ahead while you wait.";
  } else {
    elements.turnTitle.textContent = "The next turn is starting.";
  }

  elements.roomActionButton.textContent = roomActionInFlight
    ? "Working…"
    : seated
      ? "Leave game"
      : snapshot.membershipStatus === "removed"
        ? "Rejoin room"
        : "Join game";
  elements.roomActionButton.disabled = roomActionInFlight || !navigator.onLine || (!seated && roomFull);
  elements.roomActionButton.title = !seated && roomFull ? "This game currently has all 10 seats filled." : "";
  elements.roomActionButton.classList.toggle("is-leave", seated);
  elements.turnClock.classList.toggle("is-yours", seated && snapshot.turn.isYours && !resetting);
  elements.turnClock.classList.toggle("is-paused", resetting || snapshot.turn.currentMembershipId === null);
  renderQueue(snapshot);
  renderMultiplayerGrid(snapshot);
  setNotice();
}

function renderDailyGrid(snapshot: DailySnapshot): void {
  const pressed = new Set(snapshot.pressedIndices);
  const pending = new Set([...dailyQueued, ...dailyInFlight]);
  const queueFull = pending.size >= 10;
  for (let index = 0; index < dailyButtons.length; index += 1) {
    const button = dailyButtons[index]!;
    const isPressed = pressed.has(index);
    const isQueued = pending.has(index);
    const isInflight = dailyInFlight.includes(index);
    const isHidden = snapshot.hiddenIndex === index && snapshot.status !== "active";
    button.className = "grid-button";
    button.classList.toggle("is-pressed", isPressed);
    button.classList.toggle("is-queued", isQueued);
    button.classList.toggle("is-inflight", isInflight);
    button.classList.toggle("is-hidden", isHidden);
    button.disabled = !navigator.onLine || snapshot.status !== "active" || isPressed || isQueued || queueFull;
    button.setAttribute("aria-pressed", String(isPressed));
    button.setAttribute("aria-label", `Button ${index + 1}${isPressed ? ", safely pressed" : isQueued ? ", queued" : isHidden ? ", hidden right button" : ""}`);
  }
}

function renderDaily(snapshot: DailySnapshot): void {
  daily = snapshot;
  updateDailyOffset(snapshot);
  elements.dailyDate.textContent = new Intl.DateTimeFormat("en", { month: "short", day: "numeric", timeZone: "UTC" }).format(new Date(`${snapshot.playDate}T12:00:00Z`));
  elements.dailyScore.textContent = String(snapshot.safeScore);
  elements.dailyQueuedCount.textContent = String(dailyQueued.length + dailyInFlight.length);
  elements.dailySurvival.textContent = `${snapshot.survivalChance}%`;
  elements.dailyBest.textContent = String(snapshot.stats.bestScore);
  elements.dailyCompleted.textContent = String(snapshot.stats.completedDays);
  elements.dailyPerfects.textContent = String(snapshot.stats.perfectRuns);

  if (snapshot.status === "active") {
    elements.dailyKicker.textContent = "Today’s attempt";
    elements.dailyStatusTitle.textContent = snapshot.safeScore === 0 ? "Keep pressing wrong." : `${snapshot.safeScore} safe. Keep going?`;
    elements.dailyResult.hidden = true;
  } else if (snapshot.status === "perfect") {
    elements.dailyKicker.textContent = "Perfect run";
    elements.dailyStatusTitle.textContent = "Every wrong button. Incredible.";
    elements.dailyResult.hidden = false;
    elements.dailyResultKicker.textContent = "Perfect daily result";
    elements.dailyResultTitle.textContent = "You found all 99 wrong buttons.";
    elements.dailyResultCopy.textContent = "Only a 1% chance of getting all the way through without being right.";
  } else if (snapshot.status === "found") {
    elements.dailyKicker.textContent = "Attempt finished";
    elements.dailyStatusTitle.textContent = "Being right ruined it.";
    elements.dailyResult.hidden = false;
    elements.dailyResultKicker.textContent = "Today’s final score";
    elements.dailyResultTitle.textContent = `You stayed wrong ${snapshot.safeScore} time${snapshot.safeScore === 1 ? "" : "s"}.`;
    elements.dailyResultCopy.textContent = `Only a ${snapshot.survivalChance}% chance of making it that far. Come back after the UTC reset for a new board.`;
  } else {
    elements.dailyKicker.textContent = "Attempt expired";
    elements.dailyStatusTitle.textContent = "A new UTC day has started.";
    elements.dailyResult.hidden = true;
  }
  elements.dailyFinalScore.textContent = String(snapshot.safeScore);
  renderDailyGrid(snapshot);
  setNotice();
}

async function loadLeaderboard(): Promise<void> {
  if (!api) return;
  try {
    leaderboard = await api.getLeaderboard();
    renderLeaderboard();
  } catch {
    elements.myRank.textContent = "Leaderboard temporarily unavailable.";
  }
}

async function loadActiveRooms(): Promise<void> {
  if (!api || roomsLoading) return;
  roomsLoading = true;
  elements.refreshRooms.disabled = true;
  try {
    activeRooms = await api.listActiveRooms();
    renderActiveRooms();
    setNotice();
  } catch (error) {
    setNotice(`The active games could not load: ${errorText(error)}`, true);
  } finally {
    roomsLoading = false;
    elements.refreshRooms.disabled = false;
    renderActiveRooms();
  }
}

async function stopRoomConnection(): Promise<void> {
  window.clearInterval(heartbeatTimer);
  heartbeatTimer = 0;
  const channel = roomChannel;
  roomChannel = null;
  subscribedRoomId = null;
  if (api && channel) await api.unsubscribe(channel);
}

function startHeartbeat(): void {
  window.clearInterval(heartbeatTimer);
  heartbeatTimer = window.setInterval(() => {
    if (!api || !room || room.membershipStatus === "left") return;
    void api.heartbeat(room.roomId).catch(() => undefined);
  }, 10_000);
}

function scheduleRoomRefresh(version?: number): void {
  if (!room || mode === "daily") return;
  if (version !== undefined && version <= room.stateVersion) return;
  window.clearTimeout(refreshTimer);
  refreshTimer = window.setTimeout(() => void refreshRoom(), 45);
}

function subscribeToCurrentRoom(): void {
  if (!api || !room || room.membershipStatus === "left") return;
  if (subscribedRoomId === room.roomId && roomChannel) return;
  void stopRoomConnection().then(() => {
    if (!api || !room || mode === "daily" || room.membershipStatus === "left") return;
    subscribedRoomId = room.roomId;
    roomChannel = api.subscribeToRoom(
      room.roomId,
      (version, kind) => {
        scheduleRoomRefresh(version);
        if (!["preselection_changed", "player_joined"].includes(kind)) void loadLeaderboard();
      },
      (status) => {
        if (status === "SUBSCRIBED") scheduleRoomRefresh();
        if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
          setNotice("The live room is reconnecting…");
        }
      },
    );
    startHeartbeat();
  });
}

async function refreshRoom(): Promise<void> {
  if (!api || !room || roomRefreshInFlight || mode === "daily") return;
  roomRefreshInFlight = true;
  try {
    const next = await api.getRoom(room.roomId);
    renderRoom(next);
  } catch (error) {
    if (isError(error, "not_room_member") || isError(error, "room_not_found")) {
      await stopRoomConnection();
      storeRoomId(null);
      room = null;
      if (mode === "multiplayer") await joinMultiplayer();
      else void loadActiveRooms();
    } else {
      setNotice("Refreshing the shared board…");
    }
  } finally {
    roomRefreshInFlight = false;
  }
}

async function joinMultiplayer(): Promise<void> {
  if (!api) return;
  setNotice("Finding you a room…");
  elements.multiplayerMode.hidden = true;
  try {
    const savedRoomId = storedRoomId();
    if (savedRoomId) {
      try {
        const restored = await api.getRoom(savedRoomId);
        renderRoom(restored);
        elements.multiplayerMode.hidden = mode !== "multiplayer";
        subscribeToCurrentRoom();
        void loadLeaderboard();
        return;
      } catch (error) {
        if (!isError(error, "not_room_member") && !isError(error, "room_not_found")) throw error;
        storeRoomId(null);
      }
    }
    const next = await api.watchRoom();
    renderRoom(next);
    elements.multiplayerMode.hidden = mode !== "multiplayer";
    subscribeToCurrentRoom();
    void loadLeaderboard();
  } catch (error) {
    setNotice(`The multiplayer room could not start: ${errorText(error)}`, true);
  }
}

function showMode(nextMode: GameMode): void {
  mode = nextMode;
  const multiplayer = nextMode === "multiplayer";
  const rooms = nextMode === "rooms";
  const dailyMode = nextMode === "daily";
  elements.multiplayerTab.classList.toggle("is-active", multiplayer);
  elements.roomsTab.classList.toggle("is-active", rooms);
  elements.dailyTab.classList.toggle("is-active", dailyMode);
  elements.multiplayerTab.setAttribute("aria-selected", String(multiplayer));
  elements.roomsTab.setAttribute("aria-selected", String(rooms));
  elements.dailyTab.setAttribute("aria-selected", String(dailyMode));
  elements.multiplayerMode.hidden = !multiplayer;
  elements.roomsMode.hidden = !rooms;
  elements.dailyMode.hidden = !dailyMode;
}

async function watchActiveRoom(roomId: string): Promise<void> {
  if (!api || roomsLoading || !navigator.onLine) return;
  if (room?.membershipStatus === "active" && room.roomId !== roomId) {
    setNotice("Leave your current game before switching rooms.", true);
    return;
  }
  roomsLoading = true;
  let failed = false;
  renderActiveRooms();
  setNotice("Opening the live game…");
  try {
    await stopRoomConnection();
    const next = await api.watchRoom(roomId);
    renderRoom(next);
    showMode("multiplayer");
    subscribeToCurrentRoom();
    void loadLeaderboard();
  } catch (error) {
    failed = true;
    setNotice(`That game could not open: ${errorText(error)}`, true);
    subscribeToCurrentRoom();
  } finally {
    roomsLoading = false;
    if (failed) void loadActiveRooms();
    else renderActiveRooms();
  }
}

async function toggleRoomParticipation(): Promise<void> {
  if (!api || !room || roomActionInFlight || !navigator.onLine) return;
  roomActionInFlight = true;
  let failureMessage: string | undefined;
  renderRoom(room);
  try {
    const next = room.membershipStatus === "active"
      ? await api.leaveGame(room.roomId)
      : await api.joinGame(room.roomId);
    renderRoom(next);
    subscribeToCurrentRoom();
    void loadActiveRooms();
  } catch (error) {
    failureMessage = isError(error, "room_full")
      ? "This game is full. Keep watching and try again when a seat opens."
      : `The room change did not go through: ${errorText(error)}`;
    await refreshRoom();
  } finally {
    roomActionInFlight = false;
    if (room) renderRoom(room);
    if (failureMessage) setNotice(failureMessage, true);
  }
}

async function loadDaily(): Promise<void> {
  if (!api) return;
  setNotice("Preparing today’s hidden button…");
  elements.dailyMode.hidden = true;
  try {
    dailyQueued = [];
    dailyInFlight = [];
    const next = await api.getDaily();
    renderDaily(next);
    elements.dailyMode.hidden = mode !== "daily";
  } catch (error) {
    setNotice(`The Daily Challenge could not start: ${errorText(error)}`, true);
  }
}

async function switchMode(nextMode: GameMode): Promise<void> {
  if (mode === nextMode) {
    if (nextMode === "rooms") await loadActiveRooms();
    return;
  }
  showMode(nextMode);
  if (nextMode === "multiplayer") {
    await joinMultiplayer();
  } else if (nextMode === "rooms") {
    await loadActiveRooms();
  } else {
    await stopRoomConnection();
    await loadDaily();
  }
}

async function chooseMultiplayerButton(index: number): Promise<void> {
  if (!navigator.onLine || !api || !room || multiplayerSubmitting || room.membershipStatus !== "active") return;
  const resetting = roomIsResetting(room);
  if (resetting || room.round.pressedIndices.includes(index)) return;
  const wasCurrentTurn = room.turn.isYours;
  multiplayerSubmitting = true;
  try {
    const next = room.turn.isYours
      ? await api.pressButton(room, index)
      : await api.setPreselection(
        room.roomId,
        room.round.id,
        ownQueuePlayer(room)?.preselectedIndex === index ? null : index,
      );
    renderRoom(next);
    subscribeToCurrentRoom();
    if (wasCurrentTurn || next.events.some((event) => event.isHidden)) void loadLeaderboard();
  } catch (error) {
    if (isError(error, "turn_expired")) {
      await expireCurrentTurn();
    } else {
      const message = isError(error, "button_already_pressed")
        ? "That button was just taken. Choose another."
        : "That click did not go through. The board has been refreshed.";
      setNotice(message, true);
      await refreshRoom();
    }
  } finally {
    multiplayerSubmitting = false;
    if (room) renderMultiplayerGrid(room);
  }
}

async function expireCurrentTurn(): Promise<void> {
  if (!api || !room || expirationInFlight || room.turn.expiresAt === null) return;
  expirationInFlight = true;
  try {
    const next = await api.expireTurn(room.roomId, room.turn.sequence);
    renderRoom(next);
  } catch {
    await refreshRoom();
  } finally {
    expirationInFlight = false;
  }
}

function queueDailyButton(index: number): void {
  if (!navigator.onLine || !daily || daily.status !== "active") return;
  const pending = [...dailyQueued, ...dailyInFlight];
  if (pending.length >= 10 || pending.includes(index) || daily.pressedIndices.includes(index)) return;
  dailyQueued.push(index);
  renderDaily(daily);
  void flushDailyQueue();
}

async function animateDailyEvents(snapshot: DailySnapshot): Promise<void> {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches || snapshot.events.length < 2) return;
  for (const event of snapshot.events) {
    const button = dailyButtons[event.buttonIndex];
    button?.animate(
      [{ transform: "translateY(-8px)" }, { transform: "translateY(3px)" }],
      { duration: 150, easing: "ease-out" },
    );
    await new Promise((resolve) => window.setTimeout(resolve, 55));
  }
}

async function flushDailyQueue(): Promise<void> {
  if (!api || !daily || dailySubmitting || dailyQueued.length === 0 || daily.status !== "active") return;
  dailySubmitting = true;
  dailyInFlight = dailyQueued.splice(0, 10);
  renderDaily(daily);
  try {
    const next = await api.submitDaily(daily, dailyInFlight);
    await animateDailyEvents(next);
    dailyInFlight = [];
    if (next.status !== "active") dailyQueued = [];
    renderDaily(next);
  } catch (error) {
    dailyInFlight = [];
    dailyQueued = [];
    if (isError(error, "daily_expired")) {
      await loadDaily();
    } else {
      setNotice("The rapid clicks lost contact with the server. Restoring today’s board…", true);
      await loadDaily();
    }
  } finally {
    dailySubmitting = false;
    if (daily?.status === "active" && dailyQueued.length > 0) void flushDailyQueue();
  }
}

async function share(text: string): Promise<void> {
  try {
    if (navigator.share) {
      await navigator.share({ title: "Pick the Wrong Button", text });
      return;
    }
    await navigator.clipboard.writeText(text);
    setNotice("Result copied to your clipboard.");
    window.setTimeout(() => setNotice(), 1800);
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") return;
    setNotice("The result could not be shared. Please try again.", true);
  }
}

function tick(): void {
  if (mode === "multiplayer" && room) {
    elements.turnClock.classList.remove("is-danger");
    const resetting = roomIsResetting(room);
    if (resetting !== lastResetState) {
      lastResetState = resetting;
      renderRoom(room);
    }
    const now = roomNow();
    const start = room.turn.startedAt ? Date.parse(room.turn.startedAt) : null;
    const expiry = room.turn.expiresAt ? Date.parse(room.turn.expiresAt) : null;
    if (expiry === null || start === null) {
      elements.turnCountdown.textContent = "—";
    } else if (now < start) {
      elements.turnCountdown.textContent = "10.0";
    } else {
      const remaining = expiry - now;
      elements.turnCountdown.textContent = formatCountdown(remaining);
      elements.turnClock.classList.toggle("is-danger", remaining > 0 && remaining <= 3_000);
      if (remaining <= 0) void expireCurrentTurn();
    }
  }

  if (mode === "daily" && daily) {
    const remaining = Date.parse(daily.expiresAt) - dailyNow();
    const seconds = Math.max(0, Math.floor(remaining / 1000));
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    elements.dailyResetCountdown.textContent = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")} UTC`;
    if (remaining <= 0 && !dailyMidnightRefresh) {
      dailyMidnightRefresh = true;
      void loadDaily().finally(() => { dailyMidnightRefresh = false; });
    }
  }
}

elements.multiplayerTab.addEventListener("click", () => void switchMode("multiplayer"));
elements.roomsTab.addEventListener("click", () => void switchMode("rooms"));
elements.dailyTab.addEventListener("click", () => void switchMode("daily"));
elements.roomActionButton.addEventListener("click", () => void toggleRoomParticipation());
elements.refreshRooms.addEventListener("click", () => void loadActiveRooms());
elements.shareDaily.addEventListener("click", () => {
  if (!daily || !["found", "perfect"].includes(daily.status)) return;
  void share(dailyShareText(daily.safeScore, window.location.origin));
});
elements.deleteData.addEventListener("click", async () => {
  if (!api || !window.confirm("Delete this anonymous player, personal scores, streaks, and daily history? Anonymous global totals will remain.")) return;
  try {
    await stopRoomConnection();
    await api.deleteMyData();
    storeRoomId(null);
    window.location.reload();
  } catch {
    setNotice("Your anonymous data could not be deleted. Please try again.", true);
  }
});
window.addEventListener("online", () => {
  if (mode === "multiplayer") void joinMultiplayer();
  else if (mode === "rooms") void loadActiveRooms();
  else void loadDaily();
});
window.addEventListener("offline", () => setNotice("You are offline. Reconnect before pressing another button.", true));
window.addEventListener("offline", () => {
  if (room) {
    renderMultiplayerGrid(room);
    elements.roomActionButton.disabled = true;
  }
  renderActiveRooms();
  if (daily) renderDailyGrid(daily);
});

createGrid(elements.multiplayerGrid, "multiplayer-button", multiplayerButtons, (index) => void chooseMultiplayerButton(index));
createGrid(elements.dailyGrid, "daily-button", dailyButtons, queueDailyButton);

async function boot(): Promise<void> {
  const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  const key = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined;
  const turnstileKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;
  if (!url || !key) {
    setNotice("Add the Supabase environment variables to start the game.", true);
    return;
  }
  api = new GameApi(url, key);
  try {
    let token: string | undefined;
    if (turnstileKey) {
      elements.captcha.hidden = false;
      token = await requestCaptchaToken(elements.captcha, turnstileKey);
      elements.captcha.hidden = true;
    }
    await api.initializeIdentity(token);
    tickTimer = window.setInterval(tick, 100);
    await joinMultiplayer();
  } catch (error) {
    setNotice(error instanceof Error ? error.message : "The game could not start.", true);
  }
}

void boot();

window.addEventListener("pagehide", () => {
  window.clearInterval(tickTimer);
  window.clearInterval(heartbeatTimer);
});
